import { Alert, Button, Grid, Paper, Stack, Typography } from '@mui/material';
import { formatInTimeZone } from 'date-fns-tz';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RescheduledNonRecurrenceDto } from '../../../Models/Scheduling/RescheduledNonRecurrenceDto.model';
import { RescheduledRecurrenceDto } from '../../../Models/Scheduling/RescheduledRecurrenceDto.model';
import { RootState } from '../../../reducers';
import { setIsRescheduleModalOpen } from '../../../store/officeScheduler.slice';
import { setIsLoading, setShowMessage } from '../../../store/shared.slice';
import { OfficeSchedulerModeEnum } from '../../Calendar/Enums/OfficeSchedulerModeEnum';
import RescheduleEventModal from '../../Calendar/Modals/RescheduleEventModal';
import { mapEventData } from '../../Calendar/Services/CommonCalendarServices';
import { CalendarEventData } from '../../Calendar/types/CalendarEventData';
import { updateSchedulingCallItem } from '../OfficeSchedulingService';
import '../officeScheduling.css';
import MemberInfoPanel from './MemberInfoPanel';
import SchedulingButtonsPane from './SchedulingButtonsPane';
import SchedulingEngagementDisplay from './SchedulingEngagement/SchedulingEngagementDisplay';
import SchedulingEngagementInput from './SchedulingEngagement/SchedulingEngagementInput';
import MessageDisplay from '../../Shared/MessageDisplay';

interface SchedulingDashboardProps {
    mode: OfficeSchedulerModeEnum;
    getLatestListData: () => Promise<void>;
    setOpenEditor: any;
}

const SchedulingDashboard: React.FC<SchedulingDashboardProps> = ({ mode, getLatestListData, setOpenEditor }: SchedulingDashboardProps) => {
    const dispatch = useAppDispatch();
    const { associatedEvent, isRescheduleModalOpen, schedulingProvider, selectedCallItem } = useSelector((state: RootState) => state.officeSchedulerSlice);
    const [showAlert, setShowAlert] = useState(false);
    // Backend builds exclusion as `${exclusionDate}T${parentUtcTime}.000Z`, so this must be
    // the UTC calendar day of the occurrence — not the Arizona local day (which differs after 5 PM AZ).
    const exclusionDate = useMemo(() => {
        if (associatedEvent !== null) {
            return formatInTimeZone(new Date(associatedEvent.AppointmentDateTime), 'UTC', 'yyyy-MM-dd');
        } else return '';
    }, [associatedEvent]);

    useEffect(() => {
        if (associatedEvent != null) {
            const docsEl = document.getElementById('docsEl');
            docsEl.scrollTop = 0;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [associatedEvent]);

    const doSaveAndFinish = useCallback(async () => {
        const apptId = associatedEvent.Id;
        const response = await updateSchedulingCallItem(
            schedulingProvider.Id,
            apptId,
            selectedCallItem.CallStatus.split(' ').join(''),
            exclusionDate,
            selectedCallItem.LastContact ? new Date(selectedCallItem.LastContact).toISOString() : null,
            mode === OfficeSchedulerModeEnum.Rescheduling ? 'Rescheduling' : 'Confirmations',
            selectedCallItem.Note
        );
        if (response.status === 200) {
            await getLatestListData();
        }
    }, [
        associatedEvent.Id,
        schedulingProvider.Id,
        selectedCallItem.CallStatus,
        selectedCallItem.LastContact,
        selectedCallItem.Note,
        exclusionDate,
        mode,
        getLatestListData,
    ]);

    const reschedulableEvent = useMemo(() => {
        let newEvent = structuredClone(associatedEvent);
        newEvent.startDate = newEvent.AppointmentDateTime;
        newEvent.endDate = newEvent.AppointmentEndDateTime;
        const mappedData = mapEventData(newEvent) as CalendarEventData;
        mappedData.IsRescheduling = true;
        return mappedData;
    }, [associatedEvent]);

    useEffect(() => {
        if (!(selectedCallItem.Note?.length > 0) || !(selectedCallItem.CallStatus?.length > 0)) {
            setShowAlert(true);
        } else {
            setShowAlert(false);
        }
    }, [selectedCallItem.Note, selectedCallItem.CallStatus]);

    return (
        <>
            <MessageDisplay />
            <RescheduleEventModal
                eventData={reschedulableEvent}
                setLoadingOverlay={function (isLoading: boolean): void {
                    dispatch(setIsLoading(isLoading));
                }}
                open={isRescheduleModalOpen}
                onClose={() => {
                    dispatch(setIsRescheduleModalOpen(false));
                }}
                onRescheduleSuccess={(rescheduledAppointment: RescheduledNonRecurrenceDto) => {
                    dispatch(setIsRescheduleModalOpen(false));
                    dispatch(setShowMessage(true, 'Event rescheduled successfully.', 'success'));
                }}
                onRescheduleRecurrenceSuccess={(rescheduledRecurrence: RescheduledRecurrenceDto) => {
                    dispatch(setIsRescheduleModalOpen(false));
                    dispatch(setShowMessage(true, 'Recurrence instance event rescheduled successfully.', 'success'));
                }}
            />
            <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} marginBottom={2}>
                <Typography variant="h6" textAlign={'center'} component="h6">
                    Selected Scheduling Call Item
                </Typography>
            </Stack>
            <Paper elevation={3} style={{ padding: '16px 16px 16px 16px' }}>
                <MemberInfoPanel />
                <SchedulingButtonsPane mode={mode} setOpenEditor={setOpenEditor} refreshMemberData={getLatestListData} />
                <SchedulingEngagementInput />
                {showAlert && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Please click call and then an action button and leave a note to enable Save and Finish.
                    </Alert>
                )}
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!(selectedCallItem.Note?.length > 0) || !(selectedCallItem.CallStatus?.length > 0)}
                    onClick={doSaveAndFinish}
                    fullWidth
                    sx={{ marginY: 2 }}
                >
                    Save and Finish
                </Button>
                <SchedulingEngagementDisplay />
            </Paper>
        </>
    );
};

export default SchedulingDashboard;
